/**
 * WorkspaceSwitcher — Studio-mode dropdown stub.
 *
 * Wave 4 ships a presentational shell using <details>/<summary>. Wave 5 wires
 * real switching (Auth0 multi-tenant + URL re-write).
 */

import { workspaceSwitcherCopy as C } from "./WorkspaceSwitcher.copy";

export interface Workspace {
  readonly slug: string;
  readonly name: string;
}

export interface WorkspaceSwitcherProps {
  readonly currentWorkspace: Workspace | null;
  readonly workspaces?: readonly Workspace[];
  readonly onSwitch?: (slug: string) => void;
}

export function WorkspaceSwitcher({
  currentWorkspace, workspaces = [], onSwitch,
}: WorkspaceSwitcherProps): React.ReactElement {
  const label = currentWorkspace?.name ?? C.empty;
  const others = workspaces.filter((w) => w.slug !== currentWorkspace?.slug);
  return (
    <details
      data-testid="workspace-switcher"
      className="bg-stage-riser border-ledger-line rounded-ledger-line"
      style={{ position: "relative" }}
    >
      <summary
        aria-label={C.toggleLabel}
        className="font-plex-mono text-paper-cream-dim"
        style={{
          display: "flex", alignItems: "center", gap: "0.5rem",
          padding: "6px 10px", fontSize: "0.781rem",
          listStyle: "none", cursor: "pointer",
        }}
      >
        <span style={{ color: "var(--paper-cream-faint)" }}>{C.prefix}</span>
        <span className="text-paper-cream">{label}</span>
        <span aria-hidden style={{ color: "var(--brass-cue)" }}>{C.chevron}</span>
      </summary>
      {others.length > 0 ? (
        <ul
          data-testid="workspace-switcher-menu"
          style={{
            position: "absolute", top: "100%", right: 0, marginTop: "4px",
            padding: "4px 0", minWidth: "180px", listStyle: "none",
            background: "var(--stage-riser)", border: "1px solid var(--ledger-line)",
            borderRadius: "var(--radius)", zIndex: 20,
          }}
        >
          {others.map((w) => (
            <li key={w.slug}>
              <button
                type="button"
                onClick={() => onSwitch?.(w.slug)}
                className="font-plex-mono text-paper-cream"
                style={{
                  width: "100%", textAlign: "left", padding: "6px 12px",
                  fontSize: "0.781rem", background: "transparent",
                  border: "none", cursor: "pointer",
                }}
              >{w.name}</button>
            </li>
          ))}
        </ul>
      ) : null}
    </details>
  );
}

export default WorkspaceSwitcher;
